import { RiStarFill, RiStarLine } from "@remixicon/react";
import type {
  DensitySnapshot,
  InstrumentRef,
  InstrumentStatusView,
  LockedCard,
  WrappedCoin,
} from "@screener/protocol";
import { Badge } from "@screener/uikit/components/badge";
import { Card, CardContent } from "@screener/uikit/components/card";
import { LockedInline } from "@screener/uikit/components/locked-inline";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { memo, type KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef } from "react";
import { exchangeLabel, marketLabel, tickerLabel, wrappedMark } from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import { CARD_BODY_GRID_CLASS, CARD_EMPTY_TEXT_CLASS, CARD_HEADER_CLASS } from "./card-classes";
import { CardHideButton } from "./card-hide-button";
import { CardMinimumPopover } from "./card-minimum-popover";
import { CardPinButton } from "./card-pin-button";
import { cardEmptyText, cardExchangeBase } from "./card-pin-labels";
import {
  buildCards,
  CARD_COLUMN_GAP_PX,
  type CardRowOrder,
  type DensityCard,
  instrumentKey,
  type PinnedCards,
} from "./cards";
import { cardIndexOfFlatRow } from "./cards-window";
import { COIN_SCOPE, CoinNoteIndicator, noteIndicatorFor } from "./coin-note-indicator";
import { DensityCardRow, samePinMatch } from "./density-card-row";
import type { NoteIndicator } from "./density-row";
import type { DensitySort } from "./derive";
import { densityEmptyMessage } from "./derive";
import type { DisplayMinimumsState } from "./display-minimums";
import { ExchangeLogo } from "./exchange-logo";
import type { FavoriteIndicator } from "./favorite-star-button";
import { registerRowRevealer } from "./focus-handoff";
import { LockedCardItem } from "./locked-card-item";
import { type GridCard, isLockedGridCard, mergeLockedCards } from "./locked-cards";
import type { RowPinMatch } from "./pin-match";
import { useCardLayout } from "./use-card-layout";
import type { CardPinsState } from "./use-card-pins";
import { useCardWindow } from "./use-card-window";
import type { UseDisplayMinimumsResult } from "./use-display-minimums";
import { useRowNavigation } from "./use-row-navigation";
import { sameNumberList, sameStringList, useStableValue } from "./use-stable";
import { WrappedMark } from "./wrapped-mark";

/** Срок, в который показанная прокруткой строка ещё получает фокус (design stage9-cards-virtual Д4). */
const REVEAL_FOCUS_TIMEOUT_MS = 1000;

/**
 * Вид «карточки» §9 (1.21) — основной вид экрана: живые плотности собраны
 * по инструменту §3.2, вместо строк, разбросанных сортировкой по таблице.
 * Отбор и сопоставления заметок/закреплений/избранного приходят уже
 * готовыми — те же, что получает `DensityTable` (design.md change
 * stage6-density-cards, решение 1); порядок карточек и выбор представителя
 * каждой считает `buildCards` по сортировке экрана (design review, блок B).
 */

interface DensityCardsProps {
  /** Личные минимумы §59 — значок порога в шапке читает минимум монеты и пишет его же. */
  displayMinimums: DisplayMinimumsState;
  onPutMinimum: UseDisplayMinimumsResult["put"];
  onRemoveMinimum: UseDisplayMinimumsResult["remove"];
  /** «Повторить» окна при непрочитанном списке. */
  onReloadMinimums: () => void;
  /**
   * Сетка замороженного вида (§77, `applyFreeze`): порядок, состав и порядок
   * строк — снимка, закрытые карточки — на момент нажатия; живые числа
   * наложены. `null` — вид не заморожен, карточки строятся из `rows`.
   */
  frozenCards: readonly GridCard[] | null;
  /**
   * Закрытые карточки тарифа (протокол 12) после фильтров экрана. Стоят в
   * сетке среди открытых; при замороженном виде не используются.
   */
  lockedCards: readonly LockedCard[];
  rows: DensitySnapshot[];
  /** Сколько строк было до фильтров — чтобы отличить «не пропустили» от «нет данных». */
  totalRows: number;
  /** Сортировка §9 — тот же порядок, что видит таблица и список ситуаций. */
  sort: DensitySort;
  /** Порядок строк внутри карточки §9 (1.23) — «стакан» или «по объёму». */
  rowOrder: CardRowOrder;
  instruments: InstrumentStatusView[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Приветствие ещё не пришло: вида нет вовсе, а не он пуст. */
  awaitingHello: boolean;
  /** «Только избранные» включён, а список избранного ещё не прочитан или недоступен. */
  favoritesUnavailable: boolean;
  /** Открыть детальную панель §9 по строке. */
  onOpenRow: (row: DensitySnapshot) => void;
  /**
   * Монеты, у которых есть хотя бы одна заметка §6.4 (сопоставление клиента).
   * `null` — список заметок ещё не прочитан или недоступен.
   */
  notedBases: Set<string> | null;
  /** Закрепление §6.7, сопоставленное со строкой по `density_id` (`matchPinsToRows`). */
  pinMatches: Map<string, RowPinMatch> | null;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: (row: DensitySnapshot) => void;
  /** Закрепить уровень строки или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Монеты, отмеченные избранным целиком §6.6 (сопоставление клиента). `null` — тот же смысл. */
  favoriteBases: Set<string> | null;
  /** Поставить или снять избранное монеты строки целиком. */
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Скрыть монету целиком — значок шапки карточки и хоткей `h` строки (§75 п. 2). */
  onHideCoin: (base: string) => void;
  /** Скопировать тикер строки в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: (row: DensitySnapshot) => void;
  /** Плотность, по которой сейчас открыта детальная панель — её строка остаётся видна. */
  selectedDensityId: string | null;
  /** Индекс баз в написании биржи потока — тикер шапки карточки без плотностей (§63). */
  exchangeBases: ReadonlyMap<string, string>;
  /**
   * Закрепления карточек §65 для `buildCards`. `null` — список не прочитан
   * или состав потока ещё не получен: закреплённых карточек нет (design Д7).
   */
  pinned: PinnedCards | null;
  /** Состояние списка закреплений: значок «не прочитан» при `loading`/`unavailable`. */
  cardPinsStatus: CardPinsState["status"];
  /** Ключи карточек (`instrumentKey`), чьё закрепление ещё в пути. */
  pendingCardPinKeys: ReadonlySet<string>;
  /** База биржи из записей закреплений: запасной источник тикера. */
  cardPinExchangeBases: ReadonlyMap<string, string>;
  /** Нажатие на значок закрепления карточки; смысл решает экран. */
  onToggleCardPin: (instrument: InstrumentRef) => void;
}

function favoriteIndicatorFor(base: string, favoriteBases: Set<string> | null): FavoriteIndicator {
  return favoriteBases === null ? "unknown" : favoriteBases.has(base) ? "yes" : "no";
}

/** Значок избранного монеты в шапке карточки — тот же приём, что у заметки (`coin-note-indicator.tsx`). */
function CoinFavoriteIndicator({ base, favorite }: { base: string; favorite: FavoriteIndicator }) {
  if (favorite === "no") {
    return null;
  }
  const label =
    favorite === "yes"
      ? `${base} в избранном — ${COIN_SCOPE}`
      : `Избранное ${base}: неизвестно — список не прочитан; ${COIN_SCOPE}`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={label}
            className={cn(
              "inline-flex",
              favorite === "yes" ? "text-foreground" : "text-muted-foreground",
            )}
          />
        }
      >
        {favorite === "yes" ? <RiStarFill className="size-3" /> : <RiStarLine className="size-3" />}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface DensityCardItemProps {
  card: DensityCard;
  ticker: string;
  wrapped: readonly WrappedCoin[];
  hasNote: NoteIndicator;
  favorite: FavoriteIndicator;
  displayMinimums: DisplayMinimumsState;
  onPutMinimum: UseDisplayMinimumsResult["put"];
  onRemoveMinimum: UseDisplayMinimumsResult["remove"];
  onReloadMinimums: () => void;
  /** Закреплена ли карточка; `null` — список не прочитан. */
  pinnedState: boolean | null;
  pinBusy: boolean;
  onToggleCardPin: (instrument: InstrumentRef) => void;
  stale: boolean;
  /** `density_id` строки этой карточки, на которой стоит точка табуляции, иначе `null`. */
  focusableId: string | null;
  /** `density_id` строки этой карточки, по которой открыта панель, иначе `null`. */
  selectedId: string | null;
  pinMatches: Map<string, RowPinMatch> | null;
  onFocus: (densityId: string) => void;
  onKeyDown: (densityId: string, event: ReactKeyboardEvent<HTMLElement>) => void;
  onOpenRow: (row: DensitySnapshot) => void;
  onTogglePin: (row: DensitySnapshot) => void;
  onOpenNotes: (row: DensitySnapshot) => void;
  onCopyTicker: (row: DensitySnapshot) => void;
  onToggleFavorite: (row: DensitySnapshot) => void;
  onHideCoin: (base: string) => void;
}

/**
 * Карточки равны, когда равны их строки поэлементно по ссылке (`applyLiveValues`
 * заменяет только изменившиеся снимки) и скалярные поля, а `buildCards` каждый
 * раз отдаёт новые объекты карточек с теми же строками.
 */
/** Ушедшие строки карточки замороженного вида: наборы пересоздаются на каждый пересчёт, сравниваются по составу. */
function sameEndedIds(
  previous: ReadonlySet<string> | undefined,
  next: ReadonlySet<string> | undefined,
): boolean {
  if (previous === next) {
    return true;
  }
  const previousSize = previous?.size ?? 0;
  if (previousSize !== (next?.size ?? 0)) {
    return false;
  }
  if (previous === undefined) {
    return true;
  }
  for (const id of previous) {
    if (next?.has(id) !== true) {
      return false;
    }
  }
  return true;
}

export function sameCard(previous: DensityCard, next: DensityCard): boolean {
  if (previous === next) {
    return true;
  }
  if (
    previous.key !== next.key ||
    previous.maxUsd !== next.maxUsd ||
    previous.pinned !== next.pinned ||
    previous.hiddenCount !== next.hiddenCount ||
    previous.rows.length !== next.rows.length ||
    !sameEndedIds(previous.endedIds, next.endedIds)
  ) {
    return false;
  }
  return previous.rows.every((row, index) => row === next.rows[index]);
}

/**
 * Совпадения закреплений для строк этой карточки. Карта целиком пересоздаётся
 * на каждый пересчёт, а карточке важны лишь её строки.
 */
function samePinMatchesForCard(
  card: DensityCard,
  previous: Map<string, RowPinMatch> | null,
  next: Map<string, RowPinMatch> | null,
): boolean {
  if (previous === next) {
    return true;
  }
  if (previous === null || next === null) {
    return false;
  }
  return card.rows.every((row) =>
    samePinMatch(previous.get(row.density_id), next.get(row.density_id)),
  );
}

export function areCardItemPropsEqual(
  previous: DensityCardItemProps,
  next: DensityCardItemProps,
): boolean {
  if (!sameCard(previous.card, next.card)) {
    return false;
  }
  if (!samePinMatchesForCard(next.card, previous.pinMatches, next.pinMatches)) {
    return false;
  }
  return (
    previous.ticker === next.ticker &&
    previous.wrapped === next.wrapped &&
    previous.hasNote === next.hasNote &&
    previous.favorite === next.favorite &&
    previous.displayMinimums === next.displayMinimums &&
    previous.onPutMinimum === next.onPutMinimum &&
    previous.onRemoveMinimum === next.onRemoveMinimum &&
    previous.onReloadMinimums === next.onReloadMinimums &&
    previous.pinnedState === next.pinnedState &&
    previous.pinBusy === next.pinBusy &&
    previous.onToggleCardPin === next.onToggleCardPin &&
    previous.stale === next.stale &&
    previous.focusableId === next.focusableId &&
    previous.selectedId === next.selectedId &&
    previous.onFocus === next.onFocus &&
    previous.onKeyDown === next.onKeyDown &&
    previous.onOpenRow === next.onOpenRow &&
    previous.onTogglePin === next.onTogglePin &&
    previous.onOpenNotes === next.onOpenNotes &&
    previous.onCopyTicker === next.onCopyTicker &&
    previous.onToggleFavorite === next.onToggleFavorite &&
    previous.onHideCoin === next.onHideCoin
  );
}

const DensityCardItem = memo(function DensityCardItem({
  card,
  ticker,
  wrapped,
  hasNote,
  favorite,
  displayMinimums,
  onPutMinimum,
  onRemoveMinimum,
  onReloadMinimums,
  pinnedState,
  pinBusy,
  onToggleCardPin,
  stale,
  focusableId,
  selectedId,
  pinMatches,
  onFocus,
  onKeyDown,
  onOpenRow,
  onTogglePin,
  onOpenNotes,
  onCopyTicker,
  onToggleFavorite,
  onHideCoin,
}: DensityCardItemProps) {
  const planGate = usePlanGate();
  return (
    <Card variant="lift" size="sm">
      {/* Шапка макета «Cards Final» (§88 п. 5): одна линия 36 px без нижней
          черты и своего фона. Не `CardHeader`: его сетка в две строки
          макету не соответствует. */}
      <div className={CARD_HEADER_CLASS}>
        <ExchangeLogo exchange={card.instrument.exchange} className="h-3.5" />
        <span className="font-semibold text-[14px] tracking-[-0.005em] whitespace-nowrap">
          {ticker}
        </span>
        <WrappedMark base={card.instrument.base} wrapped={wrapped} />
        <Badge variant="outline-muted" size="tag">
          {marketLabel(card.instrument.market)}
        </Badge>
        <CoinFavoriteIndicator base={card.instrument.base} favorite={favorite} />
        <CoinNoteIndicator
          base={card.instrument.base}
          hasNote={hasNote}
          className="text-muted-foreground"
          iconClassName="size-3"
        />
        {/* Тариф без пометок ММ и подходов: демон их не шлёт, на их месте
            замок с тарифом, который их открывает (stage9-billing-gating). */}
        {planGate.mmApproachesLocked ? (
          <LockedInline label="Пометки ММ и подходы" text={planGate.mmApproachesText} />
        ) : null}
        <span className="flex-1" />
        <div className="flex items-center text-muted-foreground">
          <CardMinimumPopover
            instrument={card.instrument}
            state={displayMinimums}
            onPut={onPutMinimum}
            onRemove={onRemoveMinimum}
            onReload={onReloadMinimums}
          />
          <CardPinButton
            ticker={ticker}
            pinned={pinnedState}
            busy={pinBusy}
            onClick={() => onToggleCardPin(card.instrument)}
          />
          <CardHideButton
            base={card.instrument.base}
            onClick={() => onHideCoin(card.instrument.base)}
          />
        </div>
      </div>
      {/* Четыре дорожки строки (§9 1.45, 1.75): цена, время жизни, дистанция,
            объём. Сжимается только цена; остальные — по самой широкой записи
            карточки, но не уже ширин макета (design.md stage9-redesign-cards, Д1). */}
      <CardContent className={CARD_BODY_GRID_CLASS}>
        {card.rows.length === 0 ? (
          <p className={CARD_EMPTY_TEXT_CLASS}>{cardEmptyText(card.hiddenCount)}</p>
        ) : null}
        {card.rows.map((row) => (
          <DensityCardRow
            key={row.density_id}
            row={row}
            maxUsd={card.maxUsd}
            stale={stale}
            ended={card.endedIds?.has(row.density_id) ?? false}
            focusable={row.density_id === focusableId}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onOpen={onOpenRow}
            selected={row.density_id === selectedId}
            pinMatch={pinMatches === null ? "unknown" : pinMatches.get(row.density_id)}
            onTogglePin={onTogglePin}
            onOpenNotes={onOpenNotes}
            onCopyTicker={onCopyTicker}
            onToggleFavorite={onToggleFavorite}
            onHideCoin={onHideCoin}
          />
        ))}
      </CardContent>
    </Card>
  );
}, areCardItemPropsEqual);

export function DensityCards({
  displayMinimums,
  onPutMinimum,
  onRemoveMinimum,
  onReloadMinimums,
  frozenCards,
  lockedCards,
  rows,
  totalRows,
  sort,
  rowOrder,
  instruments,
  wrapped,
  awaitingHello,
  favoritesUnavailable,
  onOpenRow,
  notedBases,
  pinMatches,
  onOpenNotes,
  onTogglePin,
  favoriteBases,
  onToggleFavorite,
  onHideCoin,
  onCopyTicker,
  selectedDensityId,
  exchangeBases,
  pinned,
  cardPinsStatus,
  pendingCardPinKeys,
  cardPinExchangeBases,
  onToggleCardPin,
}: DensityCardsProps) {
  const planGate = usePlanGate();
  // Устаревшие инструменты пересчитываются, только когда меняется состав
  // `instruments` (статус инструмента), а не на каждый рендер экрана.
  const staleInstruments = useMemo(
    () =>
      new Set(
        instruments
          .filter((entry) => entry.status !== "live")
          .map((entry) => instrumentKey(entry.instrument)),
      ),
    [instruments],
  );

  // `rows`, `sort` и `rowOrder` стабильны между рендерами без собственных
  // изменений (`density-screen.tsx`: `rows` — `useMemo`, `sort` и `rowOrder`
  // — состояние экрана), так что пересчёт состава карточек не идёт на каждый
  // рендер экрана.
  //
  // Замороженный вид (§77) приходит готовым, и раскладка остаётся неподвижной
  // сама собой (design stage9-cards-freeze Д3): ключи карточек и число строк в
  // них те же, что в снимке, — `cardKeys` и `rowCounts` не меняются.
  const cards = useMemo(
    () =>
      frozenCards ?? mergeLockedCards(buildCards(rows, sort, rowOrder, pinned), lockedCards, sort),
    [frozenCards, rows, sort, rowOrder, pinned, lockedCards],
  );
  // Плоский список ключей «карточка за карточкой, строка за строкой» —
  // порядок чтения раскладки (сверху вниз, design stage8-cards-balanced-columns
  // Д2; совпадает с порядком карточек), а не соседство в DOM: между
  // карточками нет общих соседей, и шаг навигации ищет их по этому списку
  // (design.md решение 5).
  const flatKeys = useMemo(
    () => cards.flatMap((card) => card.rows.map((row) => row.density_id)),
    [cards],
  );
  // Срез живых величин меняет `cards`, но не состав ключей: прежняя ссылка
  // не пересчитывает карту «ключ → индекс» навигации.
  const keys = useStableValue(flatKeys, sameStringList);
  const { focusable, onFocus, onKeyDown, activeIndex } = useRowNavigation(keys, { step: "keys" });

  // Раскладка «masonry» (design review, блокер A): все карточки — прямые дети
  // одного контейнера в порядке сортировки, позиция задаётся абсолютно, высота
  // считается из числа строк (DEN-104, design stage9-cards-layout-no-measure
  // Д1). Хук вызывается безусловно, до раннего возврата ниже — иначе число
  // вызовов хуков менялось бы между пустым и непустым составом. `cardKeys` и
  // `rowCounts` — входы раскладки: пока состав, порядок и число строк прежние,
  // ссылки прежние, и раскладка не пересчитывается на каждый срез.
  const cardKeys = useStableValue(
    useMemo(() => cards.map((card) => card.key), [cards]),
    sameStringList,
  );
  const rowCounts = useStableValue(
    useMemo(() => cards.map((card) => card.rows.length), [cards]),
    sameNumberList,
  );
  const { containerRef, container, positions, tops, heights, height } = useCardLayout(
    cardKeys,
    rowCounts,
  );

  // Окно карточек (DEN-104, design stage9-cards-virtual): рисуются карточки
  // видимой полосы с запасом и закреплённые — карточка точки табуляции и
  // карточки соседних по навигации строк. Стрелка уводит фокус в соседнюю
  // карточку, и та обязана быть в DOM, даже если стоит за краем окна (Д3).
  const pinnedCards = useStableValue(
    useMemo(
      () =>
        [activeIndex - 1, activeIndex, activeIndex + 1].map((flatIndex) =>
          cardIndexOfFlatRow(rowCounts, flatIndex),
        ),
      [rowCounts, activeIndex],
    ),
    sameNumberList,
  );
  const { indexes: windowIndexes, revealRow } = useCardWindow(
    container,
    tops,
    heights,
    rowCounts,
    pinnedCards,
  );

  // Перенос фокуса после скрытия монеты (§75 п. 2) ищет строку в DOM, а её
  // карточка может стоять вне окна: список прокручивает к строке и фокусирует
  // строку, когда та нарисуется (Д4). Срок ограничен: фокус, не дождавшийся
  // кадра, не прилетает спустя минуты.
  const latestRef = useRef({ keys, rowCounts, revealRow });
  latestRef.current = { keys, rowCounts, revealRow };
  const pendingFocusRef = useRef<{ key: string; untilMs: number } | null>(null);
  useEffect(
    () =>
      registerRowRevealer((key) => {
        const latest = latestRef.current;
        const flatIndex = latest.keys.indexOf(key);
        const cardIndex = cardIndexOfFlatRow(latest.rowCounts, flatIndex);
        if (cardIndex < 0) {
          return false;
        }
        const rowsBefore = latest.rowCounts
          .slice(0, cardIndex)
          .reduce((sum, count) => sum + count, 0);
        pendingFocusRef.current = { key, untilMs: performance.now() + REVEAL_FOCUS_TIMEOUT_MS };
        latest.revealRow(cardIndex, flatIndex - rowsBefore);
        return true;
      }),
    [],
  );
  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (pending === null || container === null) {
      return;
    }
    if (performance.now() > pending.untilMs || !keys.includes(pending.key)) {
      pendingFocusRef.current = null;
      return;
    }
    const target = container.querySelector(`[data-row-key="${CSS.escape(pending.key)}"]`);
    if (!(target instanceof HTMLElement)) {
      return;
    }
    pendingFocusRef.current = null;
    // Фокус, который человек успел поставить сам — в поле, в панель, — не отнимается.
    const active = document.activeElement;
    if (active === null || active === document.body) {
      target.focus({ preventScroll: true });
    }
  });

  if (cards.length === 0) {
    const emptyMessage = densityEmptyMessage({ awaitingHello, totalRows, favoritesUnavailable });
    return <div className="p-3 text-center text-muted-foreground">{emptyMessage}</div>;
  }

  return (
    <div className="p-3">
      <div
        ref={containerRef}
        data-row-nav
        className={positions === null ? "flex flex-col" : "relative"}
        style={positions === null ? { gap: CARD_COLUMN_GAP_PX } : { height }}
      >
        {(windowIndexes === null
          ? cards
          : windowIndexes.flatMap((index) => cards[index] ?? [])
        ).map((card) => {
          const position = positions?.get(card.key);
          const placement = positions === null ? undefined : "absolute";
          const placementStyle =
            positions === null
              ? undefined
              : {
                  left: position?.left ?? 0,
                  top: position?.top ?? 0,
                  width: position?.width ?? "100%",
                };
          if (isLockedGridCard(card)) {
            // Закрытая карточка: без монеты, строк и действий. Обёртка та же,
            // что у открытой, — позицию задаёт раскладка по ключу.
            return (
              <article key={card.key} className={placement} style={placementStyle}>
                <LockedCardItem
                  locked={card.locked}
                  text={planGate.textForExchange(card.locked.exchange)}
                />
              </article>
            );
          }
          // База в написании биржи у инструмента одна: у любой его строки (§63);
          // у карточки без строк — из потока, затем из записи закрепления (Д9).
          const exchangeBase =
            card.rows[0]?.exchange_base ||
            cardExchangeBase(card.key, exchangeBases, cardPinExchangeBases);
          const ticker = tickerLabel(card.instrument, exchangeBase);
          return (
            // Обёртка-`article` рисуется здесь, а не в мемоизированном
            // содержимом: раскладка сдвигает позиции многих карточек разом,
            // и позиция в пропсах `memo` перерисовывала бы их целиком.
            <article
              key={card.key}
              aria-label={[
                exchangeLabel(card.instrument.exchange),
                ticker,
                wrappedMark(card.instrument.base, wrapped),
                marketLabel(card.instrument.market),
              ]
                .filter((part) => part !== undefined)
                .join(" ")}
              className={placement}
              style={placementStyle}
            >
              <DensityCardItem
                card={card}
                ticker={ticker}
                wrapped={wrapped}
                hasNote={noteIndicatorFor(card.instrument.base, notedBases)}
                favorite={favoriteIndicatorFor(card.instrument.base, favoriteBases)}
                displayMinimums={displayMinimums}
                onPutMinimum={onPutMinimum}
                onRemoveMinimum={onRemoveMinimum}
                onReloadMinimums={onReloadMinimums}
                pinnedState={cardPinsStatus === "ready" ? card.pinned : null}
                pinBusy={pendingCardPinKeys.has(card.key) || cardPinsStatus === "loading"}
                onToggleCardPin={onToggleCardPin}
                stale={staleInstruments.has(card.key)}
                // Точка табуляции и выбор доходят до карточки идентификатором
                // строки только в той карточке, где строка есть: смена фокуса
                // или выбора перерисовывает две карточки, а не все.
                focusableId={card.rows.find((row) => focusable(row.density_id))?.density_id ?? null}
                selectedId={
                  card.rows.find((row) => row.density_id === selectedDensityId)?.density_id ?? null
                }
                pinMatches={pinMatches}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onOpenRow={onOpenRow}
                onTogglePin={onTogglePin}
                onOpenNotes={onOpenNotes}
                onCopyTicker={onCopyTicker}
                onToggleFavorite={onToggleFavorite}
                onHideCoin={onHideCoin}
              />
            </article>
          );
        })}
      </div>
    </div>
  );
}
